import {
  useCallback,
  useMemo,
  useState,
  type Dispatch,
  type SetStateAction,
} from "react"

import {
  AVATAR_COLORS,
  getAvatarUrl,
  type FaceCustomization,
} from "@/utils/avatarIdentity"

import {
  buildFaceCustomizationPreviewUrls,
  EYES_VARIANTS,
  getAvatarSeedFromUrl,
  getRandomAvatarColorIndex,
  HAIR_VARIANTS,
  LIPS_VARIANTS,
} from "../utils/personaModalUtils"
import type { PersonaFormData } from "../utils/personaValidation"

interface UsePersonaAvatarCustomizationOptions {
  avatarUrl: string
  faceCustomization: FaceCustomization
  setFormData: Dispatch<SetStateAction<PersonaFormData>>
  createAvatarUrl?: typeof getAvatarUrl
  randomNumber?: () => number
  createRandomSeed?: () => string
}

export const usePersonaAvatarCustomization = ({
  avatarUrl,
  faceCustomization,
  setFormData,
  createAvatarUrl = getAvatarUrl,
  randomNumber = Math.random,
  createRandomSeed = () => Math.random().toString(36).substring(7),
}: UsePersonaAvatarCustomizationOptions) => {
  const [isFaceSelectorOpen, setIsFaceSelectorOpen] = useState(false)
  const [randomColorIndex] = useState(() =>
    getRandomAvatarColorIndex(randomNumber()),
  )

  const hairPreviews = useMemo(
    () =>
      buildFaceCustomizationPreviewUrls({
        avatarUrl,
        feature: "hair",
        faceCustomization,
        variants: HAIR_VARIANTS,
      }),
    [avatarUrl, faceCustomization],
  )

  const eyesPreviews = useMemo(
    () =>
      buildFaceCustomizationPreviewUrls({
        avatarUrl,
        feature: "eyes",
        faceCustomization,
        variants: EYES_VARIANTS,
      }),
    [avatarUrl, faceCustomization],
  )

  const lipsPreviews = useMemo(
    () =>
      buildFaceCustomizationPreviewUrls({
        avatarUrl,
        feature: "lips",
        faceCustomization,
        variants: LIPS_VARIANTS,
      }),
    [avatarUrl, faceCustomization],
  )

  const handleFaceCustomization = useCallback(
    (feature: keyof FaceCustomization, value: string) => {
      const nextCustomization = {
        ...faceCustomization,
        [feature]: value,
      }

      const imageUrl = createAvatarUrl(
        getAvatarSeedFromUrl(avatarUrl),
        nextCustomization,
      )

      setFormData((previous) => ({
        ...previous,
        avatar_url: imageUrl,
        face_customization: nextCustomization,
      }))
    },
    [avatarUrl, createAvatarUrl, faceCustomization, setFormData],
  )

  const handleRegenerateSeed = useCallback(() => {
    const imageUrl = createAvatarUrl(createRandomSeed(), faceCustomization)

    setFormData((previous) => ({
      ...previous,
      avatar_url: imageUrl,
    }))
  }, [createAvatarUrl, createRandomSeed, faceCustomization, setFormData])

  return {
    avatarColors: AVATAR_COLORS,
    randomColorIndex,
    isFaceSelectorOpen,
    setIsFaceSelectorOpen,
    hairPreviews,
    eyesPreviews,
    lipsPreviews,
    handleFaceCustomization,
    handleRegenerateSeed,
  }
}
